import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './App.css';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import Alert from './components/Alert';
import NotificationToasts from './components/NotificationToasts';
import Login from './components/Login';
import RegisterForm from './components/RegisterForm';
import IncidentForm from './components/IncidentForm';
import Dashboard from './components/Dashboard';
import Reports from './components/Reports';
import RoleManagement from './components/RoleManagement';
import DepartmentManagement from './components/DepartmentManagement';
import AuditLogs from './components/AuditLogs';
import EmailSettings from './components/EmailSettings';
import BrandSettings from './components/BrandSettings';
import LandingPage from './components/LandingPage';
import { setUser , logout } from './store/slices/authSlice';
import { getCurrentUser } from './services/api';
<link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet"></link>
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const mainContent = document.querySelector('.main-content');
    if (mainContent) {
      mainContent.scrollTop = 0;
    }
  }, [pathname]);

  return null;
};

const App = () => {
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth <= 900);

  useEffect(() => {
    let isMounted = true;
    const storedUserValue = localStorage.getItem('user');
    const token = localStorage.getItem('token');

    if (storedUserValue) {
      try {
        dispatch(setUser(JSON.parse(storedUserValue)));
      } catch (error) {
        localStorage.removeItem('user');
      }
    }

    if (token) {
      getCurrentUser()
        .then(({ data }) => {
          if (isMounted) {
            dispatch(setUser(data));
          }
        })
        .catch(() => {});
    }

     // Listen for the 'logout' event triggered by the API interceptor
  const handleLogout = () => {
    dispatch(logout()); // Dispatch the logout action to Redux
  };

   // Add event listener for the custom 'logout' event
   window.addEventListener('logout', handleLogout);

    // Clean up the event listener when the component unmounts
    return () => {
      isMounted = false;
      window.removeEventListener('logout', handleLogout);
    };

    
  }, [dispatch]);

 

  const PrivateRoute = ({ children, roles }) => {
    if (!isAuthenticated) {
      return <Navigate to="/login" replace />;
    }
    if (roles && !roles.includes(user?.role)) {
      return <Navigate to={roles.includes('Admin') ? '/dashboard' : '/home'} replace />;
    }
    return children;
  };

  return (
    <Router>
      <ScrollToTop />
      <div className="App d-flex flex-column min-vh-100">
        {isAuthenticated && (
          <Header
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed((value) => !value)}
          />
        )}
        <Alert />
        {isAuthenticated && <NotificationToasts />}
        
        <div className={`app-shell d-flex flex-grow-1 ${!isAuthenticated ? 'app-shell-public' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
          {isAuthenticated && (
            <Sidebar
              collapsed={sidebarCollapsed}
              onToggleSidebar={() => setSidebarCollapsed((value) => !value)}
            />
          )}
          <MainContent collapsed={sidebarCollapsed} publicView={!isAuthenticated}>
            <Routes>
              <Route
                path="/"
                element={!isAuthenticated ? <LandingPage /> : <Navigate to="/dashboard" />}
              />
              <Route
                path="/login"
                element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />}
              />
              <Route
                path="/register"
                element={
                  <PrivateRoute roles={['Admin', 'Quality Manager', 'Quality Staff']}>
                    <RegisterForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/incident"
                element={
                  <PrivateRoute>
                    <IncidentForm />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <PrivateRoute roles={['Admin', 'Quality Manager', 'Quality Staff']}>
                    <Navigate to="/reports/ovr-registry" />
                  </PrivateRoute>
                }
              />
              <Route
                path="/reports/ovr-registry"
                element={
                  <PrivateRoute roles={['Admin', 'Quality Manager', 'Quality Staff']}>
                    <Reports />
                  </PrivateRoute>
                }
              />
              <Route
                path="/roles"
                element={
                  <PrivateRoute roles={['Admin']}>
                    <RoleManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/departments"
                element={
                  <PrivateRoute roles={['Admin']}>
                    <DepartmentManagement />
                  </PrivateRoute>
                }
              />
              <Route
                path="/audit-logs"
                element={
                  <PrivateRoute roles={['Admin']}>
                    <AuditLogs />
                  </PrivateRoute>
                }
              />
              <Route
                path="/email-settings"
                element={
                  <PrivateRoute roles={['Admin']}>
                    <EmailSettings />
                  </PrivateRoute>
                }
              />
              <Route
                path="/brand-settings"
                element={
                  <PrivateRoute roles={['Admin']}>
                    <BrandSettings />
                  </PrivateRoute>
                }
              />
              
            </Routes>
          </MainContent>
        </div>
      </div>
    </Router>
  );
};

export default App;
